import React, { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import DOMPurify from 'dompurify';
import debounce from 'lodash.debounce';
import Fuse from 'fuse.js';

const SearchBox = ({ onSearchSubmit }) => {
  const [searchValue, setSearchValue] = useState('');
  const originalProducts = useSelector((state) => state.products.products);
  const [suggestions, setSuggestions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false); // Track submit state

  // Memoize Fuse instance
  const fuse = useMemo(() => new Fuse(originalProducts, {
    keys: ['itemName'],
    threshold: 0.3,
    includeScore: true,
  }), [originalProducts]);

  // Debounced function to handle input change
  const debouncedHandleChange = useCallback(
    debounce((sanitizedInput) => {
      if (sanitizedInput.trim() === '' || isSubmitting) {
        setSuggestions([]); // Clear suggestions if input is empty or submitting
        return;
      }

      // Search for products using Fuse.js
      const results = fuse.search(sanitizedInput.trim());
      const suggestionResults = results.map((result) => result.item); // Extract the items from the results

      setSuggestions(suggestionResults);
    }, 300),
    [fuse, isSubmitting] // Make sure debounce knows about submit state
  );

  const handleChange = (e) => {
    const value = e.target.value;
    const sanitizedInput = DOMPurify.sanitize(value);
    setSearchValue(sanitizedInput);

    if (sanitizedInput.trim() === '') {
      // If the search field is cleared, show all products
      setSuggestions([]);
      onSearchSubmit(''); // This signals the parent to show all products
    } else {
      debouncedHandleChange(sanitizedInput);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchValue(suggestion.itemName);
    setSuggestions([]); // Clear suggestions on selection
    onSearchSubmit(suggestion.searchName); // Trigger search submit with selected suggestion
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedValue = searchValue.trim();
    if (trimmedValue) {
      setIsSubmitting(true); // Set submitting flag to true
      setSuggestions([]); // Clear suggestions on submit
      onSearchSubmit(trimmedValue.toLowerCase()); // Trigger search submit
    }
  };

  // Reset submitting state after search is performed
  const resetSubmitState = () => {
    setIsSubmitting(false);
  };

  return (
    <div className="container mx-auto pt-6">
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <input
            className="searchInput w-full px-4 py-2 border border-gray-300 rounded-lg"
            type="search"
            placeholder="Search your favorite products"
            value={searchValue}
            onChange={handleChange}
            style={{
              color: 'var(--text-header-color)', // Uses the text header color (dark gray)
              background: '', // Placeholder background color
            }}
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-lg"
          >
            <FontAwesomeIcon
              icon={faSearch}
              style={{ color: '' }} // Icon color uses header text color
            />
          </button>
        </div>

        {suggestions.length > 0 && !isSubmitting && ( // Only show suggestions when not submitting
          <ul
            className="absolute w-full bg-white border border-gray-300 rounded-lg shadow-lg  overflow-y-auto z-10"
            style={{ color: 'var(--text-header-color)' }} // Suggestions text color
          >
            {suggestions.map((suggestion, index) => (
              <li
                key={`${suggestion.itemName}-${suggestion.id || index}`} // Use itemName and fallback to index if id is missing
                className="p-2 cursor-pointer hover:bg-gray-200"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion.itemName}
              </li>
            ))}
          </ul>
        )}
      </form>
    </div>
  );
};

export default SearchBox;
